/**
 * @Module: Price Firestore Service
 * @Status: Ready
 * @Summary: CRUD for user ingredient prices in Firestore
 * @Description: Every method requires a uid — throws AUTH_REQUIRED if missing.
 *   Documents live at /users/{uid}/ingredientPrices/{priceId}. save() uses addDoc
 *   for new entries (no id) and updateDoc for existing ones. getByName() does a
 *   full scan with client-side filter — acceptable for a personal price registry
 *   (typically < 200 items).
 * @NextSteps: Add Firestore index on name for server-side where() queries
 */

import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  type Firestore,
} from 'firebase/firestore';
import type { IngredientPrice } from '../../types/ingredient-price';
import { PRICE_SCHEMA_VERSION } from '../../types/ingredient-price';
import { AppError, wrapFirestoreError } from './errors';

function assertUid(uid: string | null | undefined): asserts uid is string {
  if (!uid) throw new AppError('AUTH_REQUIRED', 'Sign in to manage prices');
}

function pricesCol(db: Firestore, uid: string) {
  return collection(db, 'users', uid, 'ingredientPrices');
}

function priceDoc(db: Firestore, uid: string, priceId: string) {
  return doc(db, 'users', uid, 'ingredientPrices', priceId);
}

export function createPriceService(db: Firestore) {
  return {
    /**
     * Save an ingredient price. If price.id is empty, creates a new doc.
     * If price.id is set, updates the existing doc.
     */
    async save(uid: string | null, price: IngredientPrice): Promise<IngredientPrice> {
      assertUid(uid);

      try {
        const firestoreData = {
          name: price.name,
          displayName: price.displayName,
          price: price.price,
          quantity: price.quantity,
          unit: price.unit,
          costPerBaseUnit: price.costPerBaseUnit,
          baseUnit: price.baseUnit,
          category: price.category,
          source: price.source,
          recordedAt: price.recordedAt,
          updatedAt: serverTimestamp(),
          schemaVersion: PRICE_SCHEMA_VERSION,
        };

        if (!price.id) {
          // New entry — addDoc generates an ID
          const ref = await addDoc(pricesCol(db, uid), firestoreData);
          return { ...price, id: ref.id, updatedAt: new Date().toISOString() };
        }

        // Existing entry — update in place
        await updateDoc(priceDoc(db, uid, price.id), firestoreData);
        return { ...price, updatedAt: new Date().toISOString() };
      } catch (error) {
        wrapFirestoreError(error, 'price.save');
      }
    },

    /** List all ingredient prices for a user, newest first. */
    async list(uid: string | null): Promise<IngredientPrice[]> {
      assertUid(uid);

      try {
        const q = query(pricesCol(db, uid), orderBy('updatedAt', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            id: d.id,
            name: (data.name as string) ?? '',
            displayName: (data.displayName as string) ?? '',
            price: (data.price as number) ?? 0,
            quantity: (data.quantity as number) ?? 1,
            unit: (data.unit as string) ?? 'whole',
            costPerBaseUnit: (data.costPerBaseUnit as number) ?? 0,
            baseUnit: (data.baseUnit as string) ?? 'whole',
            category: (data.category as string) ?? 'other',
            source: (data.source as string) ?? 'manual',
            recordedAt: (data.recordedAt as string) ?? new Date().toISOString(),
            updatedAt: (data.updatedAt as string) ?? new Date().toISOString(),
            schemaVersion: (data.schemaVersion as number) ?? PRICE_SCHEMA_VERSION,
          } as IngredientPrice;
        });
      } catch (error) {
        wrapFirestoreError(error, 'price.list');
      }
    },

    /** Find a price entry by normalized ingredient name. Returns null if not found. */
    async getByName(uid: string | null, name: string): Promise<IngredientPrice | null> {
      assertUid(uid);

      try {
        const all = await this.list(uid);
        const normalized = name.toLowerCase();
        return all.find((p) => p.name === normalized) ?? null;
      } catch (error) {
        wrapFirestoreError(error, 'price.getByName');
      }
    },

    /** Delete an ingredient price document permanently. */
    async delete(uid: string | null, priceId: string): Promise<void> {
      assertUid(uid);

      try {
        await deleteDoc(priceDoc(db, uid, priceId));
      } catch (error) {
        wrapFirestoreError(error, 'price.delete');
      }
    },
  };
}
